import React, { FC, useCallback, useMemo, useRef } from 'react';
import { RowV2, ColV2, BoxV2, EmptyStateV2 } from '@cubeyou-team/hx-react-components';
import styled from 'styled-components';
import PieChart from '../PieChart';
import uniqid from 'uniqid';

const StyledMultiplePieChartWrapper = styled.div`
  width: 100%;
  height: 100%;
  position: relative;
  background-color: #ffffff;
  padding: 10px;
`;

type MultiplePieChartProps = {
  margin: any;
  data: any[];
  hasInnerRadius: boolean;
  metric: string;
  emptyDataMessage: string;
  limit: any;
  showItemsPreview?: boolean;
  minMarkerArcAngle?: number;
  horizontalLayout?: boolean;
  colors: any;
};
const defaultMargin = { top: 50, right: 50, bottom: 50, left: 80 };
const MultiplePieChart: FC<MultiplePieChartProps> = ({
  data,
  limit = 'auto',
  metric,
  margin = defaultMargin,
  hasInnerRadius,
  emptyDataMessage,
  horizontalLayout,
  showItemsPreview,
  minMarkerArcAngle,
  colors
}) => {
  const chartId = useRef(uniqid());
  const isEmpty = !data || data.length === 0;
  const keys = (!isEmpty && Object.keys(data[0].metrics)) || [];
  const filteredData = useMemo(() => {
    if (isEmpty) {
      return [];
    }
    return limit !== 'auto' ? data.slice(0, limit) : data;
  }, [isEmpty, data, limit]);

  const getPieChartData = useCallback(
    (key) => {
      const items = filteredData.map((d) => {
        return {
          id: d.id,
          insight_type: d.insight_type,
          label: d.label,
          pic: d.pic,
          ...d.metrics[key]
        };
      });
      return items.filter((d) => d[metric] && d[metric] > 0);
    },
    [filteredData, metric]
  );

  if (isEmpty) {
    return (
      <StyledMultiplePieChartWrapper>
        <BoxV2 minWidth='100%'>
          <EmptyStateV2
            icon='analytics'
            mainText={emptyDataMessage}
            size='large'
          />
        </BoxV2>
      </StyledMultiplePieChartWrapper>
    );
  }
  return (
    <StyledMultiplePieChartWrapper>
      <RowV2
        valign='equal'
        halign={horizontalLayout ? 'left' : 'center'}
        style={{ height: '100%' }}
      >
        {keys.map((key, index) => (
          <ColV2
            span={horizontalLayout && keys.length > 2 ? 4 : 6}
            key={`multiple-pie-chart-${chartId.current}-${index}`}
          >
            <BoxV2 style={{ height: '100%' }}>
              <PieChart
                margin={margin}
                data={getPieChartData(key)}
                hasInnerRadius={hasInnerRadius}
                metric={metric}
                color={colors[key]}
                emptyDataMessage={emptyDataMessage}
                limit={limit}
                showItemsPreview={showItemsPreview}
                minMarkerArcAngle={minMarkerArcAngle}
                limitText={10}
              />
            </BoxV2>
          </ColV2>
        ))}
      </RowV2>
    </StyledMultiplePieChartWrapper>
  );
};

export default MultiplePieChart;
